import { getContributorName, hasAlbumSignup, rememberContributorName, subscribeAlbumSignup } from './albumSignup';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { PhotoSection } from '../shared/photo';
import { api, UploadAccessError } from './api';
import { createIdempotencyKey } from './idempotency';
import { watchFileReturn } from './fileReturn';
import { markGuestDraft, pendingGuestDraft, restoreGuestDraft, saveGuestDraft } from './guestDraft';

type Item = { file: File; key: string; url: string; caption: string; status: 'ready' | 'sending' | 'done' | 'error'; error: string; previewFailed?: boolean };

export function GuestPhotos({ section, maxBytes, onBusy, onComplete, onAccessError, onPending, onViewPhotos, onGetAlbum }: {
  onViewPhotos: () => void; onGetAlbum: () => void;
  onPending: (pending: boolean) => void;
  section: PhotoSection; maxBytes: number; onBusy: (busy: boolean) => void;
  onComplete: () => void; onAccessError: (error: UploadAccessError) => void;
}) {
  const signedUp = useSyncExternalStore(subscribeAlbumSignup, hasAlbumSignup, () => false);
  const [items, setItems] = useState<Item[]>([]);
  const [name, setName] = useState(getContributorName);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [notice, setNotice] = useState('');
  const [restored, setRestored] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  const cameraPicker = useRef<HTMLInputElement>(null);
  const cameraControl = useRef<ReturnType<typeof watchFileReturn> | null>(null);
  const pickerControl = useRef<ReturnType<typeof watchFileReturn> | null>(null);
  const sharing = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const urls = useRef(new Set<string>());
  useEffect(() => {
    let stopped = false;
    const recovering = pendingGuestDraft() === section;
    void restoreGuestDraft(section).then((saved) => {
      if (stopped) return;
      if (saved?.items.length) {
        setName(saved.name);
        const recovered: Item[] = saved.items.map((item) => {
          let url = '';
          try { url = URL.createObjectURL(item.file); urls.current.add(url); } catch { /* Preview optional. */ }
          return { ...item, status: 'ready', url, error: '' };
        });
        setItems((current) => [...recovered, ...current]);
        setNotice('Your unfinished photos have been restored. Review them and tap Share to continue.');
      } else if (recovering) setNotice('Your browser restarted before the photos could be saved here. Please choose them from your phone again.');
    }).catch(() => {
      if (!stopped) setRecoveryError('A recovery copy isn’t available in this browser. Keep this page open until sharing finishes.');
    }).finally(() => { if (!stopped) setRestored(true); });
    return () => { stopped = true; };
  }, [section]);
  useEffect(() => {
    if (!restored) return;
    let current = true;
    setSavingDraft(true);
    void saveGuestDraft(section, name, items)
      .catch(() => { if (current) setRecoveryError('Your recovery copy couldn’t be saved. Keep this page open until sharing finishes.'); })
      .finally(() => { if (current) setSavingDraft(false); });
    return () => { current = false; };
  }, [restored, section, name, items]);
  useEffect(() => () => { urls.current.forEach((url) => URL.revokeObjectURL(url)); }, []);
  const done = items.filter((item) => item.status === 'done').length;
  const pending = items.filter((item) => item.status !== 'done');
  const duplicates = new Set<string>();
  const seenFiles = new Set<string>();
  for (const item of items) {
    const signature = JSON.stringify([item.file.name, item.file.size, item.file.lastModified]);
    if (seenFiles.has(signature) && item.status !== 'done') duplicates.add(item.key);
    seenFiles.add(signature);
  }
  useEffect(() => {
    onPending(pending.length > 0);
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    if (pending.length && (busy || savingDraft || recoveryError || !restored)) window.addEventListener('beforeunload', warn);
    return () => { window.removeEventListener('beforeunload', warn); onPending(false); };
  }, [pending.length, onPending, busy, savingDraft, recoveryError, restored]);
  useEffect(() => {
    if (!picker.current) return;
    const receive = (files: File[]) => {
      const accepted: Item[] = [];
      let rejected = 0;
      for (const file of files) {
        if (file.size === 0 || file.size > maxBytes || (!file.type.startsWith('image/') && !/\.(jpe?g|png|webp|gif|avif|heic|heif)$/i.test(file.name))) { rejected++; continue; }
        let url = '';
        try { url = URL.createObjectURL(file); urls.current.add(url); } catch { /* Preview is optional. */ }
        accepted.push({ file, key: createIdempotencyKey(), url, caption: '', status: 'ready', error: '' });
      }
      setItems((current) => [...current, ...accepted]);
      setNotice(rejected ? `${rejected} file(s) couldn’t be added. Choose photos smaller than ${Math.round(maxBytes / 1024 / 1024)} MB each.` : '');

    };
    const missing = () => setNotice('No photos came back from your phone. Please choose them again. Your current selections are still here.');
    const control = watchFileReturn(picker.current, document, window, receive, missing);
    const capture = cameraPicker.current ? watchFileReturn(cameraPicker.current, document, window, receive, missing) : null;
    pickerControl.current = control;
    cameraControl.current = capture;
    return () => { control.dispose(); capture?.dispose(); pickerControl.current = null; cameraControl.current = null; };
  }, [maxBytes]);
  function patch(key: string, update: Partial<Item>) {
    setItems((current) => current.map((item) => item.key === key ? { ...item, ...update } : item));
  }
  async function share() {
    if (!restored || sharing.current || !name.trim() || !pending.length) return;
    sharing.current = true;
    setBusy(true); onBusy(true); setNotice('');
    try {
      for (const [index, item] of pending.entries()) {
        if (!mounted.current) break;
        patch(item.key, { status: 'sending', error: '' });
        try {
          await api.upload(item.file, item.caption, item.key,
            (value) => setProgress(`Uploading photo ${index + 1} of ${pending.length} · ${value}%`),
            name.trim(), section, () => setProgress(`Saving photo ${index + 1} of ${pending.length}…`));
          patch(item.key, { status: 'done' });
          rememberContributorName(name);
        } catch (error) {
          patch(item.key, { status: 'error', error: error instanceof Error ? error.message : 'Please try again.' });
          if (error instanceof UploadAccessError) { onAccessError(error); break; }
        }
      }
      if (mounted.current) onComplete();
    } finally { sharing.current = false; setBusy(false); onBusy(false); setProgress(''); }
  }
  if (done > 0 && pending.length === 0 && !busy) return <div className="guest-photos guest-share-complete">
    <p className="eyebrow">Thank you for sharing</p>
    <h2>Your {done === 1 ? 'photo is' : `${done} photos are`} in the album.</h2>
    <p role="status">{done === 1 ? 'Your photo has' : `All ${done} photos have`} been shared with {section === 'celebration' ? 'The Celebration' : 'Memories with Lloyd'}.</p>
    <button className="button full-width" onClick={onViewPhotos}>View photos</button>
    <div className="guest-success-email">
      {signedUp ? <><p role="status">You’re already on the list. We’ll email you the finished album when it’s ready.</p><button className="secondary-button full-width" onClick={onGetAlbum}>Check my email signup</button></> : <>
      <p>More memories are still coming in. Sign up to receive the finished collection.</p>
      <button className="secondary-button full-width" onClick={onGetAlbum}>Get the finished album by email</button>
      </>}
    </div>
  </div>;
  return <div className="guest-photos">
    <p className="eyebrow">{section === 'celebration' ? 'Photos from the party' : 'Memories with Lloyd'}</p>
    <h2>{items.length ? 'Ready to share?' : section === 'celebration' ? 'Add your party photos.' : 'Add a favourite memory.'}</h2>
    <p className="guest-intro">{items.length ? `${items.length} ${items.length === 1 ? 'photo selected' : 'photos selected'} · Add a caption if you like.` : 'Choose one or several photos. Review before sharing.'}</p>
    <input ref={cameraPicker} type="file" accept="image/*" capture="environment" hidden aria-label="Take a photo with Lloyd" />
    <input ref={picker} type="file" accept="image/*,.heic,.heif" multiple hidden aria-label="Choose photos" />    {!!done && <p className="guest-success" role="status">{done} {done === 1 ? 'photo has' : 'photos have'} been shared. Thank you! {pending.length ? 'Your remaining photos are below.' : 'Close this window to browse the album, or add more photos.'}</p>}
    <div className="guest-photo-grid">
      {items.map((item, index) => <article key={item.key}>
        {item.previewFailed || !item.url ? <p>Preview unavailable. You can still share this photo, or remove it and choose another.</p> : <img src={item.url} alt={`Selected photo ${index + 1}`} onError={() => patch(item.key, { previewFailed: true })} />}
        <div className="guest-photo-details">
        {item.status === 'done' ? <strong>Shared ✓</strong> : <>
          <label>Caption <span>(optional)</span><textarea maxLength={240} rows={2} value={item.caption} disabled={busy} placeholder="Add a few words…" onChange={(event) => patch(item.key, { caption: event.target.value })} /></label>
          {duplicates.has(item.key) && <p className="guest-duplicate" role="status">Possible duplicate: a photo with the same filename, size and date is already in this selection. Remove this copy if you don’t need it.</p>}
          <button className="text-button guest-remove" aria-label={`Remove photo ${index + 1}`} disabled={busy} onClick={() => { URL.revokeObjectURL(item.url); urls.current.delete(item.url); setItems((current) => current.filter((entry) => entry.key !== item.key)); }}>×</button>
          {item.error && <p className="form-error" role="alert">{item.error}</p>}
        </>}</div>
      </article>)}
    </div>
    {notice && <p role="alert">{notice}</p>}
    {recoveryError && <p role="status">{recoveryError}</p>}
    <button className={items.length ? 'secondary-button full-width guest-add-more' : 'button full-width'} disabled={busy || !restored} onClick={() => { markGuestDraft(section); setNotice(''); pickerControl.current?.open(); }}>{!restored ? 'Checking saved photos…' : items.length ? <><span className="guest-add-icon" aria-hidden="true"><svg viewBox="0 0 20 20" fill="none"><path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg></span><span>Add more photos</span></> : 'Choose photos'}</button>
    {!items.length && section === 'memories' && <button className="text-button guest-camera" disabled={busy || !restored} onClick={() => { markGuestDraft(section); setNotice(''); cameraControl.current?.open(); }}>Or take a photo with Lloyd</button>}
    {!!pending.length && <>
      <label className="guest-name">Your name<input autoComplete="name" maxLength={80} required value={name} disabled={busy} onChange={(event) => setName(event.target.value)} /></label>
      <p className="fine-print">Your photos, name and captions will be visible to album visitors.</p>
      <button className="button full-width" disabled={busy || !name.trim()} onClick={() => void share()}>{busy ? 'Sharing…' : `${pending.some((item) => item.status === 'error') ? 'Retry' : 'Share'} ${pending.length} ${pending.length === 1 ? 'photo' : 'photos'}`}</button>
    </>}
    {busy && <p role="status">{progress || 'Starting upload…'} Please keep this page open.</p>}
  </div>;
}
